import { Text } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { Button } from "../../../components/Button";
import { PageWrapper } from "../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../components/StickyScrollPageHeader";
import { PageContent } from "../../../WorkerShifts/components/PageContent";
import { useHiringInstantPay } from "../../hooks/useHiringInstantPay";
import { PLACEMENT_PATHS } from "../../paths";

export function HiringInstantPayTrialPage() {
  const history = useHistory();
  const { userId: workerId } = useDefinedWorker();

  useLogEffect(
    APP_V2_APP_EVENTS.HIRING_INSTANT_PAY_TRIAL_PAGE_VIEWED,
    { workerId },
    { enabled: true }
  );

  const handleContinue = useCallback(() => {
    logEvent(APP_V2_APP_EVENTS.HIRING_INSTANT_PAY_TRIAL_LETS_DO_IT_CLICKED, { workerId });
    history.push(PLACEMENT_PATHS.HIRING_INSTANT_PAY_FORM);
  }, [history, workerId]);

  const { enabled } = useHiringInstantPay();

  useEffect(() => {
    if (!enabled) {
      history.push(PLACEMENT_PATHS.ROOT);
    }
  }, [enabled, history]);

  if (!enabled) {
    return null;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper sx={{ backgroundColor: (theme) => theme.palette.background.paper }}>
        <StickyScrollPageHeader title="" headerVariant="primary" />

        <PageContent>
          <Stack
            sx={{
              height: "100%",
              paddingX: 5,
              paddingY: 8,
              maxWidth: "600px",
              marginX: "auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* Main content */}
            <Stack spacing={6}>
              <Text
                variant="h3"
                sx={{
                  fontWeight: 600,
                  textAlign: "center",
                  lineHeight: 1.4,
                }}
              >
                How the Instant Pay trial works:
              </Text>

              <Stack
                component="ol"
                spacing={4}
                sx={{
                  paddingLeft: 5,
                  margin: 0,
                  "& li": {
                    paddingLeft: 2,
                  },
                }}
              >
                <Box component="li">
                  <Text
                    variant="body1"
                    sx={{
                      lineHeight: 1.6,
                    }}
                  >
                    Share your shift details, up to 5 shifts.
                  </Text>
                </Box>

                <Box component="li">
                  <Text
                    variant="body1"
                    sx={{
                      lineHeight: 1.6,
                    }}
                  >
                    Clipboard pays up to 60% of your earnings, minus a $5 transaction fee.
                  </Text>
                </Box>

                <Box component="li">
                  <Text
                    variant="body1"
                    sx={{
                      lineHeight: 1.6,
                    }}
                  >
                    When your payday hits, Clipboard will send you an email to pay us back.
                  </Text>
                </Box>
              </Stack>

              <Box sx={{ paddingTop: 8, paddingBottom: 2 }}>
                <Text
                  variant="h3"
                  sx={{
                    textAlign: "center",
                    marginBottom: 4,
                  }}
                >
                  That&apos;s it!
                </Text>

                <Text
                  variant="body2"
                  sx={{
                    fontStyle: "italic",
                    color: (theme) => theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  In the future, repayment will be automatic, with no action required from you.
                  Thanks for your interest in our Instant Pay trial.
                </Text>
              </Box>
            </Stack>

            {/* Button pinned to bottom */}
            <Box sx={{ paddingTop: 6 }}>
              <Button fullWidth size="large" onClick={handleContinue}>
                Let&apos;s do it!
              </Button>
            </Box>
          </Stack>
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}
